/* eslint-disable camelcase */
import { BodyText } from '@postscript/components';
import { Message, Subscriber } from 'components/sales/types';
import React from 'react';
import styled from 'styled-components';

import { IDENTITY_COPY } from '../copy';
import { MessageFeedbackControls } from '../MessageFeedbackControls';
import { MessageDetails } from '../types';

const OverviewStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
`;

// Soft lavender bubble matching the sender chat-bubble color from the
// conversation thread, per Figma spec (`var(--gray-2)` = #efedf5).
const MessageBubble = styled.div`
  background: var(--gray-2);
  border-radius: var(--border-radius-medium);
  padding: var(--spacing-2);
  white-space: pre-wrap;
  word-break: break-word;
`;

const ResponseFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`;

interface OverviewSectionProps {
  details: Pick<MessageDetails, 'full_reference_text'>;
  // The live sent message + subscriber powering the feedback affordance. When
  // omitted, the Overview renders the static "Shopper Automation" label only.
  message?: Message;
  subscriber?: Subscriber | null;
}

// Prefer the blueprint's reference text, but fall back to the live sent
// message so the preview bubble always renders, as shown in the Figma design.
export const resolvePreviewText = (
  fullReferenceText: string | null,
  messageText?: string,
): string | null => fullReferenceText ?? messageText ?? null;

// Top of the drawer: a borderless message bubble + feedback thumbs row.
// No section card and no "Overview" heading — the bubble + feedback stand
// alone above the bordered Message Inputs / Subscriber Details cards.
export const OverviewSection: React.FC<OverviewSectionProps> = ({
  details,
  message,
  subscriber = null,
}) => {
  const previewText = resolvePreviewText(
    details.full_reference_text,
    message?.text,
  );

  return (
    <OverviewStack>
      {previewText && (
        <MessageBubble>
          <BodyText size="small">{previewText}</BodyText>
        </MessageBubble>
      )}
      {message ? (
        <MessageFeedbackControls message={message} subscriber={subscriber} />
      ) : (
        <ResponseFooter>
          <BodyText size="small" color="var(--text-color-dim)">
            {IDENTITY_COPY.shopperAutomation}
          </BodyText>
        </ResponseFooter>
      )}
    </OverviewStack>
  );
};
